<script>
import { Bar } from "vue-chartjs";
import "@/plugins/chart";

export default {
    name: "BarChart",
    extends: Bar,
    props: {
        title: {
            type: String,
            default: null,
        },
        labels: {
            type: Array,
            default: () => [],
        },
        datasets: {
            type: Array,
            default: () => [],
        },
        showPercentage: {
            type: Boolean,
            default: false,
        },
        lowPerformanceDays: {
            type: Array,
            default: () => []
        }
    },
    computed: {
        data(vm = this) {
            return {
                labels: vm.labels,
                datasets: vm.datasets,
            }
        },
        hasManyDatasets(vm = this) {
            return vm.datasets.length > 1
        },
        options(vm = this) {
            const theme = vm.$vuetify.theme.currentTheme;
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    yAxes: [{
                        ticks: {
                            beginAtZero: true,
                        },
                        gridLines: {
                            display: false
                        },
                        display: false,
                        stacked: true
                    }],
                    xAxes: [{
                        ticks: {
                            display: false
                        },
                        gridLines: {
                            display: false
                        },
                        stacked: true
                    }],
                },
                legend: {
                    display: false,
                },
                title: {
                    display: !!vm.title,
                    text: vm.title
                },
                ...(vm.hasManyDatasets ? { hover: { mode: 'index', intersect: false, axis: 'x' } } : {}),
                tooltips: {
                    mode: vm.hasManyDatasets ? 'index' : 'nearest',
                    intersect: !vm.hasManyDatasets,
                    axis: 'x',
                    backgroundColor: theme.surface2,
                    titleFontColor: theme.txtPrimary,
                    bodyFontColor: theme.txtSecondary,
                    borderColor: theme.outline,
                    borderWidth: 1,
                    cornerRadius: 8,
                    xPadding: 10,
                    yPadding: 8,
                    titleFontFamily: '"Outfit", sans-serif',
                    bodyFontFamily: '"Outfit", sans-serif',
                    callbacks: {
                        title: function (tooltipItems, data) {
                            const idx = tooltipItems[0]?.index
                            if (idx == null) return ''
                            const dsWithDays = (data.datasets || []).find(ds => ds.days)
                            return dsWithDays?.days?.[idx] || ''
                        },
                        label: function (tooltipItem, data) {
                            const ds = data.datasets[tooltipItem.datasetIndex] || {}
                            const value = tooltipItem.yLabel
                            const name = ds.label || ''
                            return name ? `${value} ${name}` : `${value}`
                        }
                    }
                },
                layout: {
                    padding: {
                        top: 50,
                        bottom: 20
                    }
                },
                animation: {
                    onProgress: function () {
                        let chartInstance = this.chart;
                        let ctx = chartInstance.ctx;
                        chartInstance.data.datasets.forEach(function (dataset, i) {
                            if (dataset.centerValue) {
                                let meta = chartInstance.controller.getDatasetMeta(i);
                                meta.data.forEach(function (bar, index) {
                                    let text = dataset.data[index];
                                    if (vm.showPercentage) text = text + '%'
                                    ctx.save()
                                    ctx.font = 'bold 14px "Outfit", sans-serif';
                                    ctx.fillStyle = '#8892A4'
                                    ctx.textAlign = "center"
                                    ctx.fillText(text, bar._model.x, bar._model.y + 30);
                                    ctx.restore();
                                })
                            }
                            if (!!dataset.days) {
                                let meta = chartInstance.controller.getDatasetMeta(i);
                                meta.data.forEach(function (bar, index) {
                                    ctx.save()
                                    ctx.font = '11px "Outfit", sans-serif';
                                    ctx.fillStyle = '#8892A4'
                                    ctx.textAlign = "center"
                                    ctx.translate(bar._model.x, bar._model.y - 30);
                                    ctx.rotate(74)
                                    ctx.fillText(dataset.days[index], 0, 0);
                                    ctx.restore();
                                })
                            }
                        });
                    }
                },
            }
        }
    },
    mounted() {
        const vm = this
        vm.addPlugin({
            afterDraw: chart => {
                let ctx = chart.chart.ctx;
                let yAxis = chart.scales['y-axis-0'];
                ctx.save();
                ctx.textAlign = 'center';
                ctx.font = 'bold 13px "Outfit", sans-serif';
                ctx.fillStyle = '#8892A4';

                let minValue = null;
                if (!vm.lowPerformanceDays?.length) {
                    minValue = Math.min.apply(null, chart.data.labels);
                }

                const days = chart.data.datasets.find(ds => ds.days)?.days || [];

                chart.data.labels.forEach((label, i) => {
                    const day = String(days[i] || '');
                    let color = '#828282';

                    if (vm.lowPerformanceDays?.length) {
                        if (vm.lowPerformanceDays.includes(day)) {
                            color = 'rgba(255, 73, 85, .7)';
                        }
                    } else {
                        if (label === minValue) {
                            color = 'rgba(255, 73, 85, .7)';
                        }
                    }

                    ctx.fillStyle = color;

                    let meta = chart.getDatasetMeta(0);
                    if (meta && meta.data[i]) {
                        let x = meta.data[i]._model.x;
                        ctx.fillText(label, x, yAxis.bottom + 17);
                    }
                });

                ctx.restore();
            }
        })
        vm.renderChart(vm.data, vm.options);
    },
    methods: {
        rerender() {
            const vm = this
            vm.renderChart(vm.data, vm.options);
        }
    },
    watch: {
        data() {
            this.rerender()
        },
        options() {
            this.rerender()
        }
    }
};
</script>